import { Box } from '@chakra-ui/react';
import { isBrowser } from 'constants/browser';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { DragEvent } from 'react';
import { WidgetType } from 'types/widget.types';
import { stopPropagation } from 'utils/event';
import { DRAG_DROP_SUPPORTED_WIDGET_TYPES } from 'widgets/Widget.config';

import { createGhostPreview, destroyGhostPreview, dropInWidget } from './WidgetDragDrop.helpers';
import { DropInWidgetData, PageAreaRef, TrackEventData, WidgetDragDropWrapperProps } from './WidgetDragDrop.types';

export const WidgetDragDropWrapper = ({
  children,
  dragImage,
  isDraggable = true,
  widgetData,
  type,
  wrapperContentProps,
  onDragStart = () => {},
  dragImageOffset,
  trackingEventData,
  mapConfig,
}: WidgetDragDropWrapperProps) => {
  const handleDragStart = async (e: DragEvent<HTMLDivElement>) => {
    if (!DRAG_DROP_SUPPORTED_WIDGET_TYPES.includes(type)) {
      stopPropagation(e);
      return;
    }

    if (widgetData) {
      // create ghost preview image with custom dragImage if dragImage is set
      // FIXME: disable on Safari for now until dragImage is working properly
      if (dragImage && !isBrowser.SAFARI) createGhostPreview(e, dragImage, widgetData, dragImageOffset);

      // add tracking data to widget data transfer
      if (trackingEventData) Object.assign(widgetData, { trackingEventData });

      // add map button config to widget data transfer for map chart widgets
      if (type === WidgetType.MapChart && mapConfig) {
        Object.assign(widgetData, { mapConfig });
      }
      // sets widget data for drag event data transfer
      e.dataTransfer.setData('application/json', JSON.stringify(widgetData));
    }

    onDragStart(e);
  };

  return (
    <Box
      draggable={isDraggable}
      onDragStart={handleDragStart}
      overflow='hidden'
      userSelect='none'
      // Needed to allow outline for nested buttons during focus events
      padding='2px'
      {...wrapperContentProps}
    >
      {children}
    </Box>
  );
};

WidgetDragDropWrapper.displayName = 'WidgetDragDropWrapper';

export const onWidgetDrop = async (e: DragEvent<HTMLDivElement>, pageAreaRef: PageAreaRef) => {
  // receive widget data from drag event data transfer
  if (e.dataTransfer.getData('application/json')) {
    const widgetData = JSON.parse(e.dataTransfer.getData('application/json')) as DropInWidgetData;

    // check if widget data exists before drop in function
    if (widgetData?.widgetData) {
      await dropInWidget(widgetData, pageAreaRef, { x: e.clientX, y: e.clientY });
    }

    // check if tracking event data exists before firing MP tracking event
    if (widgetData?.trackingEventData) {
      const { event_name, properties } = widgetData.trackingEventData as TrackEventData;
      Mixpanel.track(event_name, properties);
    }
  }

  destroyGhostPreview();
};
